<template>
  <template v-if="!!formInputs?.form?.common">
    <v-card class="my-4 pa-4">
      <v-card-title class="headline">
        {{ i18n.global.t('pages.vehicles.form.iniFileSetting.common.title') }}
      </v-card-title>
      <v-card-text>
        <div class="row mr-3 v-theme--light">
          <SettingFormInput
            :label="i18n.global.t('pages.vehicles.deviceId')"
            v-model="form.machine_id"
            :isShow="formFieldOptions.machineId.isShow"
            :isRequired="formFieldOptions.machineId.isRequired"
            :formInputs="formInputs"
            :readonly="true"
            :errors="formFieldOptions.machineId.errors"
          />
          <template v-for="(title, key) in keys" v-bind:key="key">
            <template v-if="!isOperationButtonKeys(key) && !isPoweroffKeys(key) && !isLongDriveInterruptionMode(key)">
              <div class="col" v-if="key === 'camera_on'">
                <v-select
                  hide-details="auto"
                  class="small mt-2"
                  :label="title + (formFieldOptions[key].isRequired ? ' *' : '')"
                  v-model="form['camera_on']"
                  :items="selectCameraOn"
                  :clearable="false"
                  :disabled="(selectDefault && readonlyInputs.includes('camera_on')) || isInstallationOffice"
                  :error-messages="formFieldOptions[key].errors"
                  v-show="formFieldOptions[key].isShow"
                />
              </div>
              <SettingFormInput
                :label="title"
                v-model="form[key]"
                :isShow="formFieldOptions[key].isShow"
                :isRequired="formFieldOptions[key].isRequired"
                :formInputs="formInputs"
                :readonly="(selectDefault && readonlyInputs.includes(key)) || isInstallationOffice"
                :errors="formFieldOptions[key].errors"
                v-else-if="key !== 'camera_on'"
              >
                <template v-if="key === 'overwrite_deny'">
                  <pre style="color: blue">
                    {{ i18n.global.t('pages.vehicles.form.iniFileSetting.common.allAllow') }}
                    {{ i18n.global.t('pages.vehicles.form.iniFileSetting.common.allDeny') }}
                    {{ i18n.global.t('pages.vehicles.form.iniFileSetting.common.normalDeny') }}
                    {{ i18n.global.t('pages.vehicles.form.iniFileSetting.common.eventDeny') }}
                    {{ i18n.global.t('pages.vehicles.form.iniFileSetting.common.digitachoDeny') }}
                  </pre>
                </template>
                <template v-if="key === 'upload_trip_data'">
                  <pre style="color: blue">
                    {{ i18n.global.t('pages.vehicles.form.iniFileSetting.common.sampleInput') }}{{ twoDaysAgo }},{{ yesterday }},{{ today }}
                  </pre>
                </template>
              </SettingFormInput>
            </template>
          </template>
        </div>

        <div class="mr-3 v-theme--light">
          <Toggle
            :label="keys['automatic_cancellation_on']"
            v-model="form['automatic_cancellation_on']"
            :formInputs="formInputs"
            :isShow="formFieldOptions['automatic_cancellation_on'].isShow"
            :isRequired="formFieldOptions['automatic_cancellation_on'].isRequired"
            :disabled="(selectDefault && readonlyInputs.includes('automatic_cancellation_on')) || isInstallationOffice"
          />
        </div>
        <div class="row align-items-center no-padding" v-if="form['automatic_cancellation_on'] === 'ON'">
          <template v-for="(title, key) in filteredOperationButtonKeys" v-bind:key="key">
            <SettingFormInput
              type="number"
              :label="title"
              v-model="form[key]"
              :isShow="formFieldOptions[key].isShow"
              :isRequired="formFieldOptions[key].isRequired"
              :min="1"
              :max="operationButtonMax(key)"
              colClassNumber="6"
              colClassNumberMobile="12"
              :formInputs="formInputs"
              :readonly="(selectDefault && readonlyInputs.includes('key')) || isInstallationOffice"
              :errors="formFieldOptions[key].errors"
              v-if="key !== 'automatic_cancellation_on'"
            />
          </template>
        </div>

        <div class="mr-3 v-theme--light">
          <Toggle
            :label="keys['delay_poweroff_on']"
            v-model="form['delay_poweroff_on']"
            :formInputs="formInputs"
            :isShow="formFieldOptions['delay_poweroff_on'].isShow"
            :isRequired="formFieldOptions['delay_poweroff_on'].isRequired"
            :disabled="(selectDefault && readonlyInputs.includes('delay_poweroff_on')) || isInstallationOffice"
          />
        </div>
        <div class="row align-items-center no-padding" v-if="form['delay_poweroff_on'] === 'ON'">
          <template v-for="(title, key) in filteredPoweroffButtonKeys" v-bind:key="key">
            <SettingFormInput
              type="number"
              :label="title"
              :title="poweroffVoltageTitle(key)"
              v-model="form[key]"
              :isShow="formFieldOptions[key].isShow"
              :isRequired="formFieldOptions[key].isRequired"
              :min="poweroffMin(key)"
              :max="poweroffMax(key)"
              :isDecimal="key === 'delay_poweroff_voltage'"
              colClassNumber="6"
              colClassNumberMobile="12"
              :formInputs="formInputs"
              :readonly="(selectDefault && readonlyInputs.includes('key')) || isInstallationOffice"
              :errors="formFieldOptions[key].errors"
            />
          </template>
        </div>

        <v-divider class="my-4"></v-divider>

        <div class="fs-6 fw-bold mt-4 mb-4 opacity-75">
          {{ i18n.global.t('pages.vehicles.form.iniFileSetting.common.longDriveLabel') }}
        </div>
        <div class="row align-items-center no-padding">
          <div class="col-12 col-lg-6">
            <v-select
              hide-details="auto"
              class="small mt-2"
              v-model="form['long_drive_interruption_mode']"
              :items="selectLongDriveInterruptionMode"
              :clearable="false"
              :disabled="(selectDefault && readonlyInputs.includes('long_drive_interruption_mode') && readonlyInputs.includes('long_drive_interruption_mode')) || isInstallationOffice"
            >
              <template #label>
                {{ i18n.global.t('pages.vehicles.form.iniFileSetting.common.longDriveInterruptionMode') }}
                <span v-if="formFieldOptions['long_drive_interruption_mode'].isRequired" class="text-red">
                  <strong>*</strong>
                </span>
              </template>
            </v-select>
          </div>
          <div class="col-12 col-lg-6">
            <v-select
              hide-details="auto"
              class="small mt-2"
              v-model="form['long_drive_interruption_counter_reset']"
              :items="selectLongDriveCounterReset"
              :clearable="false"
              :disabled="(selectDefault && readonlyInputs.includes('long_drive_interruption_counter_reset') && readonlyInputs.includes('long_drive_interruption_counter_reset')) || isInstallationOffice"
            >
              <template #label>
                {{ i18n.global.t('pages.vehicles.form.iniFileSetting.common.longDriveInterruptionCounterReset') }}
                <span v-if="formFieldOptions['long_drive_interruption_counter_reset'].isRequired" class="text-red">
                  <strong>*</strong>
                </span>
              </template>
            </v-select>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </template>
</template>

<script>
import SettingFormInput from '@/components/VehicleFormParts/SettingFormInput.vue';
import Toggle from "@/components/VehicleFormParts/SettingFormToggle.vue";
import i18n from '@/lang/i18n';
import { formatDate } from '@/services/functions';

export default {
  setup() {
    return {
      i18n
    }
  },
  props: {
    modelValue: {},
    formInputs: {},
    selectDefault: {
      type: Boolean,
      default: false,
    },
    errors: {},
    selfOfficeType: null,
    isInstallationOffice: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {},
      readonlyInputs: [],
      errorKeyPrefix: 'initial_params.common',
      selectOperationButtonOnOff: [
       { title: 'ON', value: 'ON' },
       { title: 'OFF', value: 'OFF' },
      ],
      selectLongDriveInterruptionMode: [
        { title: i18n.global.t('pages.vehicles.form.iniFileSetting.common.selectLongDriveInterruptionModeA'), value: 'A' },
        { title: i18n.global.t('pages.vehicles.form.iniFileSetting.common.selectLongDriveInterruptionModeB'), value: 'B' },
      ],
      selectLongDriveCounterReset: [
        { title: i18n.global.t('pages.vehicles.form.iniFileSetting.common.selectLongDriveCounterReset1'), value: '1' },
        { title: i18n.global.t('pages.vehicles.form.iniFileSetting.common.selectLongDriveCounterReset2'), value: '2' },
      ],
      selectCameraOn: [
        { title: i18n.global.t('pages.vehicles.form.iniFileSetting.common.selectCameraOnOn'), value: 'ON' },
        { title: i18n.global.t('pages.vehicles.form.iniFileSetting.common.selectCameraOnOff'), value: 'OFF' },
      ],
      keys: {
        ftp_on: i18n.global.t('pages.vehicles.form.iniFileSetting.common.ftpOn'),
        ftp_id: i18n.global.t('pages.vehicles.form.iniFileSetting.common.ftpId'),
        ftp_pass: i18n.global.t('pages.vehicles.form.iniFileSetting.common.ftpPass'),
        alert_idling_first_sec: i18n.global.t('pages.vehicles.form.iniFileSetting.common.alertIdlingFirstSec'),
        alert_idling_second_sec: i18n.global.t('pages.vehicles.form.iniFileSetting.common.alertIdlingSecondSec'),
        volume: i18n.global.t('pages.vehicles.form.iniFileSetting.common.volume'),
        alert_idling_sound: i18n.global.t('pages.vehicles.form.iniFileSetting.common.alertIdlingSound'),
        gps_dr: i18n.global.t('pages.vehicles.form.iniFileSetting.common.gpsDr'),
        camera_on: i18n.global.t('pages.vehicles.form.iniFileSetting.common.cameraOn'),
        overwrite_deny: i18n.global.t('pages.vehicles.form.iniFileSetting.common.overwriteDeny'),
        wakeup_update: i18n.global.t('pages.vehicles.form.iniFileSetting.common.wakeupUpdate'),
        upload_trip_data: i18n.global.t('pages.vehicles.form.iniFileSetting.common.uploadTripData'),
        automatic_cancellation_on: i18n.global.t('pages.vehicles.form.iniFileSetting.common.automaticCancellationOn'),
        automatic_cancellation_speed: i18n.global.t('pages.vehicles.form.iniFileSetting.common.automaticCancellationSpeed'),
        automatic_cancellation_time: i18n.global.t('pages.vehicles.form.iniFileSetting.common.automaticCancellationTime'),
        delay_poweroff_on: i18n.global.t('pages.vehicles.form.iniFileSetting.common.delayPoweroffOn'),
        delay_poweroff_time: i18n.global.t('pages.vehicles.form.iniFileSetting.common.delayPoweroffTime'),
        delay_poweroff_voltage: i18n.global.t('pages.vehicles.form.iniFileSetting.common.delayPoweroffVoltage'),
        long_drive_interruption_mode: i18n.global.t('pages.vehicles.form.iniFileSetting.common.longDriveInterruptionMode'),
        long_drive_interruption_counter_reset: i18n.global.t('pages.vehicles.form.iniFileSetting.common.longDriveInterruptionCounterReset'),
      },
    };
  },
  methods: {
    isOperationButtonKeys(key) {
      return key === 'automatic_cancellation_on' || key === 'automatic_cancellation_speed' || key === 'automatic_cancellation_time';
    },
    isPoweroffKeys(key) {
      return key === 'delay_poweroff_on' || key === 'delay_poweroff_time' || key === 'delay_poweroff_voltage';
    },
    isLongDriveInterruptionMode(key) {
      return key === 'long_drive_interruption_mode' || key === 'long_drive_interruption_counter_reset'
    },
    operationButtonMax(key) {
      return key === 'automatic_cancellation_speed' ? 20 : 30;
    },
    poweroffMin(key) {
      return key === 'delay_poweroff_time' ? 1 : 10.0;
    },
    poweroffMax(key) {
      return key === 'delay_poweroff_time' ? 30 : 24.0;
    },
    poweroffVoltageTitle(key) {
      return key === 'delay_poweroff_voltage' ? 'The acceptable input range is ' + this.poweroffMin(key).toFixed(1) + ' to ' +  this.poweroffMax(key).toFixed(1) + '.' : ''
    },
    getShowAndRequiredAndErrors(parentKey, formKeys) {
      if (!this.formInputs?.form[parentKey]) {
        return {
          'isShow': false,
          'isRequired': false,
          'errors': null
        };
      }

      const keys = Object.keys(this.formInputs.form[parentKey]);

      const result = formKeys.map(formKey => {
        const directErrors = this.errors?.[this.errorKeyPrefix + '.' + formKey];

        const indexedErrors = [];
        if (this.errors) {
          const prefix = this.errorKeyPrefix + '.' + formKey + '.';
          Object.keys(this.errors).forEach(errorKey => {
            if (errorKey.startsWith(prefix)) {
              const index = parseInt(errorKey.slice(prefix.length));
              if (!isNaN(index)) {
                const rawValue = this.form?.[formKey];
                const value = formKey === 'upload_trip_data'
                  ? rawValue?.split(',')?.[index]
                  : rawValue?.[index];
                this.errors[errorKey].forEach(msg => {
                  indexedErrors.push(`${value}: ${msg}`);
                });
              }
            }
          });
        }

        const errors = [
          ...(directErrors ?? []),
          ...indexedErrors,
        ];
        const normalizedErrors = errors.length > 0 ? errors : null;

        return {
          'isShow': keys.includes(formKey),
          'isRequired': !!this.formInputs.form[parentKey][formKey]?.required,
          'errors': normalizedErrors
        };
      });

      return {
        'isShow': result.every(item => item.isShow),
        'isRequired': result.every(item => item.isRequired),
        'errors': result.map(item => item.errors).filter(error => error).flat()
      };
    }
  },
  computed: {
    filteredOperationButtonKeys() {
      const allowedKeys = [
        'automatic_cancellation_speed',
        'automatic_cancellation_time'
      ];

      return Object.fromEntries(
          Object.entries(this.keys).filter(([key]) => allowedKeys.includes(key))
      );
    },
    filteredPoweroffButtonKeys() {
      const allowedKeys = [
        'delay_poweroff_time',
        'delay_poweroff_voltage'
      ];

      return Object.fromEntries(
          Object.entries(this.keys).filter(([key]) => allowedKeys.includes(key))
      );
    },
    formFieldOptions() {
      const parentKey = 'common'

      const options = {
        machineId: this.getShowAndRequiredAndErrors(parentKey, ['machine_id']),
      };

      Object.keys(this.keys).forEach(key => {
        options[key] = this.getShowAndRequiredAndErrors(parentKey, [key]);
      });

      return options;
    },
    today() {
      return formatDate(new Date(), '');
    },
    yesterday() {
      const date = new Date()
      date.setDate(date.getDate() - 1);
      return formatDate(date, '');
    },
    twoDaysAgo() {
      const date = new Date()
      date.setDate(date.getDate() - 2);
      return formatDate(date, ''); 
    },
  },
  watch: {
    modelValue: {
      handler() {
        this.form = this.modelValue;
      },
    },
    formInputs: {
      handler() {
        if (!this.formInputs?.form?.common) return;
        const key = Object.keys(this.formInputs.default_params)[0];
        this.readonlyInputs = Object.keys(
          this.formInputs.default_params[key]?.parameters?.common
        );
      },
    },
    form: {
      handler() {
        this.$emit('update:modelValue', this.form);
      },
      deep: true,
    },
  },
  components: {
    Toggle,
    SettingFormInput,
  },
};
</script>

<style scoped>
.headline {
  font-size: 24px;
  font-weight: bold;
  color: #0088a6;
}
.no-padding {
  padding: 0 !important;
}
</style>
